import * as React from 'react';
import { Eye, EyeOff, LockKeyhole } from 'lucide-react';
import type { FieldErrors, UseFormReturn } from 'react-hook-form';
import { Button, Input, Label, toast } from '@shigo-ui/ui';

import { dashboardT, type DashboardMessageKey } from '../../../../../lib/messages';
import type { AdminUserFormValue } from './UserDialog.schema';
import { generateSecurePassword } from './password';

interface UserPasswordFieldProps {
  form: UseFormReturn<AdminUserFormValue>;
  errors: FieldErrors<AdminUserFormValue>;
  pending: boolean;
  optional?: boolean;
}

export function UserPasswordField({
  form,
  errors,
  pending,
  optional = false,
}: UserPasswordFieldProps) {
  const [showPassword, setShowPassword] = React.useState(false);

  const passwordMessage = errors.password?.message;
  let helperText: React.ReactNode = null;
  let describedBy: string | undefined;
  if (passwordMessage) {
    describedBy = 'admin-user-password-error';
    helperText = (
      <p id={describedBy} className="text-destructive text-xs">
        {dashboardT(passwordMessage as DashboardMessageKey)}
      </p>
    );
  } else if (optional) {
    describedBy = 'admin-user-password-hint';
    helperText = (
      <p id={describedBy} className="text-muted-foreground text-xs">
        {dashboardT('users.passwordEditHint')}
      </p>
    );
  }

  return (
    <div className="space-y-1.5">
      <Label className="text-[13px] font-semibold" htmlFor="admin-user-password">
        {dashboardT('users.password')}{' '}
        {optional ? null : <span className="text-destructive">*</span>}
      </Label>
      <div className="flex gap-2">
        <div className="relative min-w-0 flex-1">
          <Input
            id="admin-user-password"
            type={showPassword ? 'text' : 'password'}
            required={!optional}
            className="pr-10"
            aria-invalid={Boolean(errors.password)}
            aria-describedby={describedBy}
            {...form.register('password')}
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="absolute top-0 right-0 h-9 w-9"
            aria-label={dashboardT(showPassword ? 'users.hidePassword' : 'users.showPassword')}
            onClick={() => {
              setShowPassword((visible) => !visible);
            }}
          >
            {showPassword ? (
              <EyeOff className="h-4 w-4" aria-hidden="true" />
            ) : (
              <Eye className="h-4 w-4" aria-hidden="true" />
            )}
          </Button>
        </div>
        <Button
          type="button"
          variant="outline"
          disabled={pending}
          onClick={() => {
            form.setValue('password', generateSecurePassword(), {
              shouldDirty: true,
              shouldValidate: true,
            });
            setShowPassword(true);
            toast.success(dashboardT('users.passwordGenerated'));
          }}
        >
          <LockKeyhole className="h-4 w-4" aria-hidden="true" />
          {dashboardT('users.generatePassword')}
        </Button>
      </div>
      {helperText}
    </div>
  );
}
